import axios from "axios";
import {
  apiBaseUrl,
  getAccessToken,
  redirectToLogin,
  refreshAccessToken,
} from "./authSession";

// Re-exported under its historical name so the ~58 modules importing { imgUrl } keep working.
export const imgUrl = apiBaseUrl;

const axiosInstance = axios.create({ baseURL: `${imgUrl}/` });

axiosInstance.interceptors.request.use(
  async (config) => {
    const token = getAccessToken();
    config.headers = {
      ...config.headers,
      Authorization: `Bearer ${token}`,
    };
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Single-flight refresh.
 *
 * Access tokens are short-lived, so a screen that fires ten parallel requests will see ten
 * simultaneous 401s. Without this gate each one would start its own refresh and they would race.
 * Instead the first 401 performs the refresh and the rest await the same promise.
 */
let refreshPromise = null;

const runRefresh = () => {
  if (!refreshPromise) {
    refreshPromise = refreshAccessToken().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error?.config;

    // Opt-in escape hatch. By default this interceptor resolves failures with `undefined`, which
    // means a caller doing `response.data` blows up on a TypeError and the backend's own message is
    // lost — every failure reaches the UI as a silent no-op. A request that passes
    // `surfaceErrors: true` gets the real rejection instead, so the caller can show that message.
    const surfaceErrors = originalRequest?.surfaceErrors === true;

    // A network failure carries no response at all — reading .status off it used to throw here.
    if (!error?.response) {
      return surfaceErrors ? Promise.reject(error) : undefined;
    }

    if (error.response.status === 401 && originalRequest && !originalRequest._retry) {
      // Marked before awaiting: if the retried request 401s again it must fall through to logout
      // rather than start another refresh cycle.
      originalRequest._retry = true;

      const refreshed = await runRefresh();

      if (refreshed?.accessToken) {
        originalRequest.headers = {
          ...originalRequest.headers,
          Authorization: `Bearer ${refreshed.accessToken}`,
        };
        return axiosInstance(originalRequest);
      }

      // Refresh token missing, expired, or revoked — the session is genuinely over.
      redirectToLogin();
      return;
    }

    // Every other failure keeps this interceptor's pre-existing behaviour of resolving with
    // undefined. Callers across the app are written against that, so switching to Promise.reject
    // here would surface errors that are currently swallowed.
    if (error.response.status === 401) {
      redirectToLogin();
      return;
    }

    if (surfaceErrors) {
      return Promise.reject(error);
    }
  }
);

export default axiosInstance;
